import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { SJDatabase } from '@/shared/lib/sj-orm/core';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { log } from '@/shared/utils/log';

export const getDtoRelatedToTargetDto = (
  sjDb: SJDatabase,
  targetDto: BaseDto,
  searchForIncludedDtos: boolean = true,
  currentMap: Map<CollectionName, Set<BaseDto>> = new Map(),
  initialDto?: BaseDto,
): Map<CollectionName, Set<BaseDto>> => {
  // eslint-disable-next-line no-param-reassign
  if (!initialDto) initialDto = targetDto;
  for (const currentCollectionName of Object.keys(sjDb.collections)) {
    const collectionName = currentCollectionName as CollectionName;

    const allowedCollections = [
      CollectionName.SOWE,
      CollectionName.SOWE_ATTACHMENTS,
      CollectionName.ENCRYPTED_FILE_KEY,
      CollectionName.CONTACTS,
      CollectionName.FILE_META_INFO,
      CollectionName.ASSET_INFORMATION_DOCUMENT,
      CollectionName.PRIVATE_DOCUMENTS,
      CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
      CollectionName.INTERWILLS,
      ...(!searchForIncludedDtos ? [CollectionName.ASSETS] : []),
      ...(!searchForIncludedDtos ? [CollectionName.ASSET_INFORMATION_DOCUMENT] : []),
    ];

    if (!allowedCollections.includes(collectionName)) continue;

    const collection = sjDb.collections[collectionName];
    const allDtos: Array<BaseDto> = collection?.findMany(() => true) || [];

    for (const dto of allDtos) {
      if (isDtoAlreadyInMap(dto, currentMap)) continue;
      if (dto.id === initialDto.id) continue;

      if (isDtoRelatedToTargetDto(targetDto, dto, searchForIncludedDtos)) {
        const dtoSet = currentMap.get(collectionName) || new Set<BaseDto>();
        dtoSet.add(dto);
        currentMap.set(collectionName, dtoSet);

        log.trace(`Found related dto: ${dto.id} in collection ${collectionName}`);
      }
    }
  }

  log.trace('getDtoRelatedToTargetDto', currentMap);
  return currentMap;
};

export function isDtoAlreadyInMap(dto: BaseDto, map: Map<CollectionName, Set<BaseDto>>): boolean {
  for (const dtoSet of map.values()) {
    if (dtoSet.has(dto)) return true;
  }

  return false;
}

function dtoHasReferenceTo(dto: BaseDto, id: string | number): boolean {
  for (const field of Object.keys(dto)) {
    const value = dto[field as keyof typeof dto];
    if (isObjectWithId(value, id)) {
      return true;
    }
  }
  return false;
}

function isDtoRelatedToTargetDto(
  targetDto: BaseDto,
  expectedRelatedDto: BaseDto,
  searchForIncludedDtos: boolean,
): boolean {
  if (targetDto.id === expectedRelatedDto.id) return false;

  if (searchForIncludedDtos && dtoHasReferenceTo(targetDto, expectedRelatedDto.id)) {
    return true;
  }

  if (dtoHasReferenceTo(expectedRelatedDto, targetDto.id)) {
    return true;
  }

  return false;
}

function isObjectWithId(value: unknown, id: string | number): boolean {
  if (value === null) return false;
  if (value === id) return true;

  if (Array.isArray(value)) {
    return value.some((item) => isObjectWithId(item, id));
  }

  if (typeof value === 'object') {
    return Object.values(value).some((item) => isObjectWithId(item, id));
  }

  return false;
}
